import { stringify } from 'querystring';
import { Filter } from 'components/molecules/filters/types';
import {
  DateRange,
  getValueFromDateRange,
} from 'components/organisms/projects-report/common/range-picker/types';
import { getTemplateIdFromFilter } from 'components/organisms/template-insight/utils/common';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';
import { HttpClient } from '../http-client';
import { DateType, DEFAULT_DATE_FORMAT } from '../models';

const RESOURCE_URL = '/reports/templates';

type Template = {
  template: {
    templateId: number;
    templateName: string;
  };
};

type CompletionTime = {
  completionTime: {
    averageDuration: number;
    p50: number;
    p90: number;
  };
};

type ProjectInfo = {
  projectId: number;
  projectName: string;
  startDate: string;
  startDateActual: string;
  dueDate: DateType;
  dueDateActual: DateType;
};

type TimeTrackedProjectInfo = ProjectInfo & {
  timeEntryReportInfo?: {
    totalTracked: number;
    totalBillable: number;
    totalNonBillable: number;
    category: {
      categoryId: number;
      categoryName?: string;
      numberOfEntries?: number;
      totalMinutesTracked?: number;
    };
  };
};

type TaskInfo = {
  taskId: number;
  taskName: string;
  startDate: DateType;
  dueDate: DateType;
  completedAt: number;
};

type TimeTrackedTaskInfo = TaskInfo & {
  timeEntryReportInfo?: {
    totalTracked: number;
    totalBillable: number;
    totalNonBillable: number;
    category: {
      categoryId: number;
      categoryName: string;
      numberOfEntries: number;
      totalMinutesTracked: number;
    };
  };
};

type PhaseInfo = {
  phaseId: number;
  phaseName: string;
  startDate: DateType;
  startDateActual?: DateType;
  dueDate: DateType;
  completedAt?: number;
};

type SourceInfo = {
  sourceId: number;
  sourceName: string;
};
type GroupByPhase = Template &
  CompletionTime & {
    phaseTemplate: SourceInfo;
    phases: Array<PhaseInfo>;
    duration: number;
    phasesCompletedLate: number;
    averageCSAT: number;
  };

type GroupByProjects = Template &
  CompletionTime & {
    projects: Array<ProjectInfo>;
    duration: number;
    projectsCompletedLate: number;
    averageCSAT: number;
  };

interface AbstractGroupByTaskFrequency extends Template {
  taskTemplate: SourceInfo & {
    responsible?: string;
  };
  tasks: {
    taskId: number;
    taskName: string;
    startDate: DateType;
    dueDate: DateType;
    completedAt: number;
  }[];
  duration: number;
}

type GroupByTaskFrequentlyCompletedEarly = AbstractGroupByTaskFrequency &
  CompletionTime & {
    tasksCompletedEarly: number;
    tasksCompletedLate: never;
  };

type TimeMeta = {
  plannedEffort: number;
  plannedEffortPercentage: number;
  excessPlanned: number;
  averageMinutesTracked: number;
  averageBillableMinutes: number;
  averageBillableMinutesPercentage: number;
  averageNonBillableMinutes: number;
  averageNonBillableMinutesPercentage: number;
  categories?: Array<{
    categoryId: number;
    categoryName: string;
    numberOfEntries: number;
    totalMinutesTracked: number;
    averageMinutesTracked: number;
    averageMinutesTrackedPercentage: number;
  }>;
};
type GroupByTaskTimeTracked = Template &
  TimeMeta & {
    tasks: Array<TimeTrackedTaskInfo>;
    taskTemplate: SourceInfo;
    phaseTemplate: SourceInfo;
  };

type GroupByProjectTimeTracked = Template &
  TimeMeta & {
    projects: Array<TimeTrackedProjectInfo>;
    budgetedHours: number;
  };

interface GroupByTaskFrequentlyCompletedLate
  extends AbstractGroupByTaskFrequency {
  tasksCompletedLate: number;
  tasksCompletedEarly: never;
  completionTime: {
    averageDuration: number;
    p50: number;
    p90: number;
  };
}

interface GroupByTaskFrequentlyBlocked extends AbstractGroupByTaskFrequency {
  noOfTimesMarked: number;
  percentageMarked: number;
  duration: number;
  averageDuration: number;
}

export enum TemplateInsightGroupBy {
  Project = 'projects',
  Phases = 'phases',
  TasksCompletedEarly = 'tasks_completed_early',
  TasksCompletedLate = 'tasks_completed_late',
  TasksBlocked = 'tasks_marked_blocked',
  TaskTimeTracked = 'tasks_time_tracked',
  ProjectTimeTracked = 'projects_time_tracked',
}

export interface TemplateInsightResponses<
  T extends TemplateInsightGroupBy,
  R = T extends TemplateInsightGroupBy.Phases
    ? GroupByPhase
    : T extends TemplateInsightGroupBy.Project
    ? GroupByProjects
    : T extends TemplateInsightGroupBy.TasksCompletedEarly
    ? GroupByTaskFrequentlyCompletedEarly
    : T extends TemplateInsightGroupBy.TasksCompletedLate
    ? GroupByTaskFrequentlyCompletedLate
    : T extends TemplateInsightGroupBy.TasksBlocked
    ? GroupByTaskFrequentlyBlocked
    : T extends TemplateInsightGroupBy.TaskTimeTracked
    ? GroupByTaskTimeTracked
    : T extends TemplateInsightGroupBy.ProjectTimeTracked
    ? GroupByProjectTimeTracked
    : never,
> {
  templateResponses: Array<R>;
  start: DateType;
  end: DateType;
  total: number;
  count: number;
  groupBy: T;
}

class TemplateInsightHttpClient extends HttpClient {
  public getTemplateReport = <T extends TemplateInsightGroupBy>({
    groupBy,
    range,
    filter,
  }: {
    groupBy: TemplateInsightGroupBy;
    range: DateRange;
    filter: Filter;
  }): Promise<TemplateInsightResponses<T>> => {
    const [startDate, endDate] = getValueFromDateRange(range).map((x) =>
      x.format(DEFAULT_DATE_FORMAT),
    );
    const templates = getTemplateIdFromFilter(filter);

    return this.get(
      `${RESOURCE_URL}?${ApiFilter.toQueryStringFormat(
        filter.criteria.filter((x) => x.field.fieldName !== 'template'),
      )}&${stringify({
        startDate,
        endDate,
        groupBy,
        count: 10_000_000,
        templates,
      })}`,
    );
  };
}

export const TemplateInsightAPI = new TemplateInsightHttpClient();
